import { useFetcher, useResolvedPath } from "@remix-run/react";
import { Button } from "../button.component";
import {
  MagnifyingGlassIcon,
  PhotoIcon,
  XCircleIcon,
} from "@heroicons/react/20/solid";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  ChangeEvent,
  ChangeEventHandler,
  KeyboardEvent,
  ReactNode,
} from "react";
import {
  DocumentIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import type { File } from "prisma-types";
import { CrudTextAreaField } from "./textarea.component";
import { Toast } from "../toast.component";
import { CTA } from "../cta.component";
import AddVideoUrlModal from "../modals/add-video-url-modal.component";
import { tv } from "tailwind-variants";
import _ from "lodash";
import { tvField } from "~/utils/global-tailwind-variants.utils";
import { IMAGE_EXTENSIONS, VIDEO_EXTENSIONS } from "~/utils/constants.utils";
import { Tooltip } from "../tooltip-overlay.component";
import { PdfPreview } from "../pdf-preview.component";
import { FormWithUploads } from "./form-with-uploads.component";
import useIsMobile from "~/hooks/useIsMobile.hook";

type CrudFormFieldBase = {
  name: string;
  label: ReactNode;
  showOptional?: boolean;
  tooltipText?: ReactNode;
  errors: string[];
  disabled?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
};

export type CrudFormFieldText = CrudFormFieldBase & {
  type: "text" | "number";
  description?: string;
  defaultValue: string | number | undefined;
  placeholder?: string;
  maxCharacters?: number;
  showMaxCharacters?: boolean;
  reactiveDefaultValue?: boolean;
  warning?: ReactNode;
  min?: number;
  max?: number;
  autoFocus?: boolean;
  autoComplete?: string;
};

export type CrudFormFieldWebsite = CrudFormFieldBase & {
  type: "website";
  placeholder: string;
  defaultValue: string | number | undefined;
};

export type CrudFormFieldTextArea = CrudFormFieldBase & {
  type: "textarea";
  defaultValue: string | number | undefined;
  description?: string;
  placeholder?: string;
  rows?: number;
  maxCharacters?: number;
};

export type CrudFormFieldSelect = CrudFormFieldBase & {
  type: "select";
  defaultValue?: string | number | string[];
  value?: string | number | string[];
  options: (
    | { label: string; value: string | number }
    | { group: string; options: { label: string; value: string }[] }
  )[];
  allowNull?: boolean;
  nullLabel?: string;
  multiple?: boolean;
  description?: string | ReactNode;
  reactiveDefaultValue?: boolean;
  reactiveTriggerOnChangeEvent?: boolean;
};

export type CrudFormFieldAutocomplete = CrudFormFieldBase & {
  type: "autocomplete";
  options:
    | { label: string; value: string; aliases?: string[] }[]
    | {
        group: string;
        options: {
          label: string;
          value: string;
          aliases?: string[];
        }[];
      }[];
  allowNull?: boolean;
  multiple?: boolean;
  description?: string;
  onSelect?: (value: string) => void;
  placeholder?: string;
  optionsContainerClass?: string;
  position?: "top" | "bottom";
  showOptionsOnClick?: boolean;
  showSearchIcon?: boolean;
} & (
    | {
        defaultValue: string | undefined;
        multiple?: false | undefined;
      }
    | {
        defaultValue: string[];
        multiple: true;
      }
  );

export type CrudFormFieldCheckbox = CrudFormFieldBase & {
  type: "checkbox";
  description?: string;
  defaultChecked: boolean | undefined;
  value?: string | number | undefined;
  reactiveDefaultChecked?: boolean;
  showTooltipOnLabel?: boolean;
};

export type CrudFormFieldRadioButton = CrudFormFieldBase & {
  type: "radio";
  description: string;
  defaultChecked?: boolean | undefined;
  checked?: boolean;
  value?: string | number | undefined;
};

export type CrudFormFieldFileUploadDefaultValue = Pick<
  File,
  "id" | "title" | "uri"
>;

interface VideoTitle {
  id: string;
  title: string;
}

export type CrudFormFieldFileUpload = CrudFormFieldBase & {
  type: "file-upload" | "image-upload";
  description?: string;
  buttonLabel: string;
  maxFileSize: number;
  accept?: string;
  handleCallback?: any;
  videoTitles?: VideoTitle[];
  limit?: number;
  currentCount?: number;
  showMaxError?: boolean;
  defaultNoValue?: ReactNode;
  square?: boolean;
} & (
    | {
        defaultValue: CrudFormFieldFileUploadDefaultValue | undefined;
        multiple?: false | undefined;
        allowRemoval?: boolean;
      }
    | {
        defaultValue: CrudFormFieldFileUploadDefaultValue[];
        multiple: true;
        allowRemoval?: true;
      }
  );

type CloudVideo = {
  title: string;
  url: string;
  type: "youtube";
  embedUrl: string;
};

export type CrudFormFieldYoutubeUrls = CrudFormFieldBase & {
  buttonLabel: string;
  onChange: (videos: CloudVideo[]) => void;
  defaultValue: CloudVideo[];
  limit?: number;
  currentCount?: number;
  showMaxError?: boolean;
};

export type CrudCustomField = {
  name: string;
  type: "custom";
  render: () => ReactNode;
};

export type AtomicCrudFormField =
  | CrudFormFieldText
  | CrudFormFieldTextArea
  | CrudFormFieldWebsite
  | CrudFormFieldSelect
  | CrudFormFieldCheckbox
  | CrudFormFieldRadioButton
  | CrudFormFieldFileUpload
  | CrudFormFieldAutocomplete
  | CrudCustomField;

export type CrudFormFieldGroup = {
  type: "group";
  name: string;
  fields: AtomicCrudFormField[];
};

export type CrudFormField = AtomicCrudFormField | CrudFormFieldGroup;

export type CrudFormSection = {
  title: string;
  subtitle: ReactNode;
  fields: CrudFormField[];
};

export type CrudFormConfig = {
  submitSuccess?: boolean;
  sections: CrudFormSection[];
  manager?: boolean;
  customToastMessage?: string;
  showToast?: boolean;
};

export function CrudWebsiteField({ field }: { field: CrudFormFieldWebsite }) {
  return (
    <div key={field.name} className="sm:col-span-4">
      <label
        htmlFor={field.name}
        className="block text-sm font-medium leading-6 text-gray-900"
      >
        {field.label}
        {field.showOptional && (
          <span className="font-normal text-gray-400 text-sm ml-1.5">
            (Optional)
          </span>
        )}
        {field.tooltipText && (
          <Tooltip text={field.tooltipText} position="top">
            <InformationCircleIcon className="h-5 text-gray-400 ml-1.5" />
          </Tooltip>
        )}
      </label>
      <div className="mt-2">
        <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-sky-600 sm:max-w-md">
          <span className="flex select-none items-center pl-3 text-gray-500 sm:text-sm">
            https://
          </span>
          <input
            type="text"
            name={field.name}
            id={field.name}
            className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
            placeholder={field.placeholder}
            disabled={field.disabled}
          />
        </div>
        <p
          className="mt-3 text-sm leading-6 text-red-600"
          id={`errors-${field.name}`}
        >
          {field.errors?.join(" ")}
        </p>
      </div>
    </div>
  );
}

export function CrudTextField({
  field,
  className,
  disableAutoComplete = false,
}: {
  field: CrudFormFieldText;
  className?: string;
  disableAutoComplete?: boolean;
}) {
  // Handle the character limit if maxCharacters is present
  const [characterCount, setCharacterCount] = useState(
    field.defaultValue?.toString().length ?? 0
  );

  const handleCharacterChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = event.target.value;
    setCharacterCount(inputValue.length);

    if (field.onChange) field.onChange(event);
  };

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (
      field.reactiveDefaultValue &&
      inputRef.current &&
      field.defaultValue !== undefined
    ) {
      inputRef.current.value = field.defaultValue as string;
    }
  }, [field.defaultValue, field.reactiveDefaultValue]);

  useEffect(() => {
    if (field.autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [field.autoFocus]);

  return (
    <div className={`col-span-full ${className}`}>
      <label
        htmlFor={field.name}
        className="flex items-center text-sm font-medium leading-6 text-gray-900"
      >
        {field.label}
        {field.showOptional && (
          <span className="font-normal text-gray-400 text-sm ml-1.5">
            (Optional)
          </span>
        )}
        {field.tooltipText && (
          <Tooltip text={field.tooltipText} position="top">
            <InformationCircleIcon className="h-5 text-gray-400 ml-1.5" />
          </Tooltip>
        )}
      </label>
      <div className="mt-2">
        <input
          type={field.type}
          name={field.name}
          id={field.name}
          defaultValue={field.defaultValue}
          placeholder={field.placeholder}
          className={tvField({ error: field.errors.length > 0 })}
          disabled={field.disabled}
          onChange={
            field.maxCharacters ? handleCharacterChange : field.onChange
          }
          onKeyDown={field.onKeyDown}
          maxLength={field.maxCharacters}
          ref={inputRef}
          min={field.min}
          max={field.max}
          autoComplete={disableAutoComplete ? "off" : "on"}
        />
      </div>
      <p className="mt-1 text-xs text-gray-500 px-2">{field.description}</p>
      <p
        className="mt-3 text-sm leading-6 text-red-600"
        id={`errors-${field.name}`}
      >
        {field.errors?.join(" ")}
      </p>
      {field.warning && (
        <div className="text-sm text-amber-500 col-span-full flex">
          <ExclamationCircleIcon className="h-4 mr-2 my-auto" />
          <span className="my-auto">{field.warning}</span>
        </div>
      )}
      {field.maxCharacters && field.showMaxCharacters !== false ? (
        <p
          id={`${field.name}-characterCount`}
          className="mt-1 text-xs text-gray-500"
        >
          {characterCount}/{field.maxCharacters} characters
        </p>
      ) : null}
    </div>
  );
}

export function CrudSelectField({
  field,
  onChange,
  className,
}: {
  field: CrudFormFieldSelect;
  onChange?: ChangeEventHandler<HTMLSelectElement>;
  className?: string;
}) {
  const inputRef = useRef<HTMLSelectElement | null>(null);

  useEffect(() => {
    if (field.reactiveDefaultValue && inputRef.current && field.defaultValue) {
      inputRef.current.value = field.defaultValue as string;
      if (field.reactiveTriggerOnChangeEvent) {
        const event = new Event("change", { bubbles: true });
        inputRef.current.dispatchEvent(event);
      }
    }
  }, [
    field.defaultValue,
    field.reactiveDefaultValue,
    field.reactiveTriggerOnChangeEvent,
  ]);

  return (
    <div className="col-span-full">
      <label
        htmlFor={field.name}
        className="flex items-center text-sm font-medium leading-6 text-gray-900"
      >
        {field.label}
        {field.showOptional && (
          <span className="font-normal text-gray-400 text-sm ml-1.5">
            (Optional)
          </span>
        )}
        {field.tooltipText && (
          <Tooltip text={field.tooltipText} position="top">
            <InformationCircleIcon className="h-5 text-gray-400 ml-1.5" />
          </Tooltip>
        )}
      </label>
      <div className="mt-2">
        <select
          ref={inputRef}
          id={field.name}
          name={field.name}
          className={tvField({ error: field.errors.length > 0, className })}
          defaultValue={field.defaultValue ?? "null"}
          disabled={field.disabled}
          multiple={field.multiple}
          onChange={onChange}
          value={field.value}
        >
          <option value="null" disabled={!field.allowNull}>
            {field.allowNull
              ? field.nullLabel ?? "None"
              : field.nullLabel ?? "Please select"}
          </option>
          {field.options.map(option =>
            "group" in option ? (
              <optgroup key={option.group} label={option.group}>
                {option.options.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ) : (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            )
          )}
        </select>
        {field.description && (
          <p className="text-xs text-gray-500 leading-6 py-2">
            {field.description}
          </p>
        )}
        <p
          className="mt-3 text-sm leading-6 text-red-600"
          id={`errors-${field.name}`}
        >
          {field.errors?.join(" ")}
        </p>
      </div>
    </div>
  );
}

export function CrudAutocompleteField({
  field,
  onChange,
}: {
  field: CrudFormFieldAutocomplete;
  onChange?: ChangeEventHandler<HTMLSelectElement>;
}) {
  const [autocompleteValue, setAutocompleteValue] = useState("");
  const [isOptionsContainerFocused, setIsOptionsContainerFocused] =
    useState(false);
  const [optionsContainerScrollTop, setOptionsContainerScrollTop] = useState(0);
  const optionsContainerRef = useRef<HTMLDivElement>(null);

  const OPTION_ITEM_HEIGHT = useMemo(() => 40, []);
  const MAX_VISIBLE_OPTIONS = useMemo(() => 10, []);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setAutocompleteValue(value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Select the first option when the user hits Enter
    if (e.key === "Enter") {
      e.preventDefault();
      if (!autocompleteValue) return;
      if (options.length === 0) return;

      const optionValue =
        "group" in options[0] ? options[0].options[0]?.value : options[0].value;
      if (!optionValue) return;
      onSelect(optionValue);
      setAutocompleteValue("");
    }
  };

  const handleOptionsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setOptionsContainerScrollTop(e.currentTarget.scrollTop);
  };

  useEffect(() => {
    setAutocompleteValue("");
  }, [field.defaultValue, field.multiple]);

  const optionsMatchedByAliases = useMemo(() => {
    if (!autocompleteValue) return [];
    if (field.options.length === 0) return [];

    const checkAliasMatch = (option: {
      label: string;
      value: string;
      aliases?: string[];
    }) => {
      return (option.aliases ?? []).some(alias =>
        alias.toLowerCase().includes(autocompleteValue?.toLowerCase())
      );
    };

    if ("group" in field.options[0]) {
      return field.options.flatMap(group => {
        const groupOptions = _.get(group, "options") || [];
        return groupOptions.filter(checkAliasMatch);
      });
    }
    return (
      field.options as {
        label: string;
        value: string;
        aliases?: string[];
      }[]
    ).filter(checkAliasMatch);
  }, [field.options, autocompleteValue]);

  const options = useMemo(() => {
    if (field.options.length === 0) return [];
    if (!autocompleteValue) return field.options;

    const searchTerm = autocompleteValue.toLowerCase();
    const checkMatch = (
      option:
        | {
            label: string;
            value: string;
            aliases?: string[];
          }
        | {
            group: string;
            options: { label: string; value: string; aliases?: string[] }[];
          }
    ) => {
      if ("group" in option) {
        return option.options.some(
          opt =>
            opt.label.toLowerCase().includes(searchTerm) ||
            optionsMatchedByAliases.includes(opt)
        );
      }
      return (
        option.label.toLowerCase().includes(searchTerm) ||
        optionsMatchedByAliases.includes(option)
      );
    };

    if ("group" in field.options[0]) {
      return field.options
        .map(group => {
          const groupOptions = _.get(group, "options") || [];
          return {
            ...group,
            options: groupOptions.filter(checkMatch),
          };
        })
        .filter(group => group.options.length > 0);
    }
    return field.options.filter(checkMatch);
  }, [field.options, autocompleteValue, optionsMatchedByAliases]);

  // Calculate total height considering both group headers and options
  const getTotalHeight = useMemo(() => {
    return options.reduce((total, option) => {
      if ("group" in option) {
        const optionsHeight = option.options.length * OPTION_ITEM_HEIGHT;
        return total + OPTION_ITEM_HEIGHT + optionsHeight;
      }
      return total + OPTION_ITEM_HEIGHT;
    }, 0);
  }, [options, OPTION_ITEM_HEIGHT]);

  // Calculate visible range based on scroll position
  const getVisibleRange = useCallback(() => {
    let currentHeight = 0;
    let startIndex = 0;
    let endIndex = 0;

    for (let i = 0; i < options.length; i++) {
      const option = options[i];
      const optionHeight =
        "group" in option
          ? OPTION_ITEM_HEIGHT + option.options.length * OPTION_ITEM_HEIGHT
          : OPTION_ITEM_HEIGHT;

      if (currentHeight + optionHeight > optionsContainerScrollTop) {
        startIndex = i;
        break;
      }
      currentHeight += optionHeight;
    }
    currentHeight = 0;
    for (let i = 0; i < options.length; i++) {
      const option = options[i];
      const optionHeight =
        "group" in option
          ? OPTION_ITEM_HEIGHT + option.options.length * OPTION_ITEM_HEIGHT
          : OPTION_ITEM_HEIGHT;

      currentHeight += optionHeight;
      if (
        currentHeight >
        optionsContainerScrollTop + MAX_VISIBLE_OPTIONS * OPTION_ITEM_HEIGHT
      ) {
        endIndex = i + 1;
        break;
      }
    }
    if (endIndex === 0) endIndex = options.length;

    return { startIndex, endIndex };
  }, [
    options,
    optionsContainerScrollTop,
    OPTION_ITEM_HEIGHT,
    MAX_VISIBLE_OPTIONS,
  ]);

  const { startIndex, endIndex } = getVisibleRange();
  const visibleOptions = options.slice(startIndex, endIndex);

  const onSelect = useCallback(
    (value: string) => {
      if (field.multiple) {
        // TODO
      } else {
        field.onSelect?.(value);
      }
    },
    [field]
  );

  return (
    <div
      className="col-span-full"
      onFocus={() => setIsOptionsContainerFocused(true)}
      onBlur={() => setIsOptionsContainerFocused(false)}
    >
      <label
        htmlFor={field.name}
        className="flex items-center text-sm font-medium leading-6 text-gray-900"
      >
        {field.label}
        {field.showOptional && (
          <span className="font-normal text-gray-400 text-sm ml-1.5">
            (Optional)
          </span>
        )}
        {field.tooltipText && (
          <Tooltip text={field.tooltipText} position="top">
            <InformationCircleIcon className="h-5 text-gray-400 ml-1.5" />
          </Tooltip>
        )}
      </label>
      <div className="relative w-full h-fit">
        <CrudTextField
          field={{
            name: field.name,
            label: "",
            errors: field.errors,
            type: "text",
            placeholder: field.placeholder,
            defaultValue: autocompleteValue,
            onChange: handleInputChange,
            onKeyDown: handleKeyDown,
          }}
          disableAutoComplete={true}
        />
        {field.showSearchIcon && (
          <MagnifyingGlassIcon className="mt-1 absolute right-2 top-1/2 transform -translate-y-1/2 size-5 text-sky-500" />
        )}
        {(autocompleteValue || field.showOptionsOnClick) &&
          options.length > 0 && (
            <div
              ref={optionsContainerRef}
              className={`
              ${isOptionsContainerFocused ? "" : "hidden"}
              ${
                field.position == "top" ? "bottom-4 shadow" : "top-8 shadow-lg"
              } 
              rounded-lg p-2 overflow-hidden bg-sky-50 w-3/6 mb-6 absolute overflow-y-auto max-h-56 z-50 
              ${field.optionsContainerClass}
            `}
              onScroll={handleOptionsScroll}
            >
              <div
                onMouseDown={e => e.preventDefault()}
                style={{
                  height: `${getTotalHeight}px`,
                  position: "relative" as const,
                }}
              >
                {visibleOptions.map((option, index) => {
                  let currentTop = options
                    .slice(0, startIndex + index)
                    .reduce((acc, opt) => {
                      if ("group" in opt) {
                        return (
                          acc +
                          OPTION_ITEM_HEIGHT +
                          opt.options.length * OPTION_ITEM_HEIGHT
                        );
                      }
                      return acc + OPTION_ITEM_HEIGHT;
                    }, 0);

                  if ("group" in option) {
                    const GroupHeader = () =>
                      option.options.length > 0 && (
                        <div className="text-xs font-medium text-gray-500 px-2 py-1.5 bg-sky-50 sticky top-0">
                          {option.group}
                        </div>
                      );

                    const GroupOption = ({
                      opt,
                      index,
                    }: {
                      opt: (typeof option.options)[0];
                      index: number;
                    }) => (
                      <div
                        key={index}
                        id={`${field.name}-option-${opt.value}`}
                        onClick={() => onSelect(opt.value)}
                        style={{
                          height: `${OPTION_ITEM_HEIGHT}px`,
                        }}
                        className="w-full flex items-center px-2 py-1.5 hover:bg-sky-100 rounded-md transition ease-in-out duration-100 cursor-pointer"
                      >
                        <p className="w-full truncate hyphens-auto line-clamp-1 whitespace-normal text-montserrat">
                          {opt.label}
                        </p>
                        {opt.aliases &&
                          optionsMatchedByAliases.includes(opt) && (
                            <span className="ml-1 font-thin w-full text-sm">
                              - is also called{" "}
                              <span className="font-normal">
                                {opt.aliases.find(alias =>
                                  alias
                                    .toLowerCase()
                                    .includes(autocompleteValue.toLowerCase())
                                )}
                              </span>
                            </span>
                          )}
                      </div>
                    );

                    return (
                      <div
                        key={option.group}
                        style={{
                          position: "absolute" as const,
                          top: `${currentTop}px`,
                        }}
                        className="flex flex-col h-fit w-full"
                      >
                        <GroupHeader />
                        <div className="flex flex-col gap-0.5">
                          {option.options.map((opt, index) => (
                            <>
                              <GroupOption opt={opt} index={index} />
                              {index === option.options.length - 1 && (
                                <>
                                  {(() => {
                                    const nextGroup =
                                      options[options.indexOf(option) + 1];
                                    const hasNextGroupWithOptions =
                                      "group" in (nextGroup || {}) &&
                                      (
                                        nextGroup as {
                                          group: string;
                                          options: any[];
                                        }
                                      ).options.length > 0;

                                    return (
                                      hasNextGroupWithOptions && (
                                        <hr className="my-0.5" />
                                      )
                                    );
                                  })()}
                                </>
                              )}
                            </>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={index}
                      style={{
                        position: "absolute" as const,
                        top: `${currentTop}px`,
                        height: `${OPTION_ITEM_HEIGHT}px`,
                      }}
                      id={`${field.name}-option-${option.value}`}
                      onClick={() => onSelect(option.value)}
                      className="w-full flex items-center px-2 py-1.5 hover:bg-sky-100 rounded-md transition ease-in-out duration-100 cursor-pointer"
                    >
                      <p className="w-full truncate hyphens-auto line-clamp-1 whitespace-normal text-montserrat">
                        {option.label}
                      </p>
                      {option.aliases &&
                        optionsMatchedByAliases.includes(option) && (
                          <span className="ml-1 font-thin w-full text-sm">
                            - is also called{" "}
                            <span className="font-normal">
                              {option.aliases.find(alias =>
                                alias
                                  .toLowerCase()
                                  .includes(autocompleteValue.toLowerCase())
                              )}
                            </span>
                          </span>
                        )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
      </div>
      <div className="mt-2">
        {field.description && (
          <p className="text-xs text-gray-500 leading-6 py-2">
            {field.description}
          </p>
        )}
      </div>
    </div>
  );
}

const tvUploadPreview = tv({
  base: "object-contain max-w-full h-auto flex",
});

export function CrudFileUpload({
  field,
  videoTitles,
  showPreviews = true,
}: {
  field: CrudFormFieldFileUpload;
  videoTitles?: VideoTitle[];
  showPreviews?: Boolean;
}) {
  field.multiple = field.multiple ?? false;
  const [files, setFiles] = useState<string[]>([]);
  const [errors, setErrors] = useState(field.errors);
  // Recalculate the images that need to be displayed when the value of field.defaultValue changes
  const defaultFiles = useMemo(
    () =>
      field.multiple
        ? field.defaultValue
        : field.defaultValue
        ? [field.defaultValue]
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [field.multiple, JSON.stringify(field.defaultValue)]
  );

  const fetcher = useFetcher();
  const { pathname: action } = useResolvedPath("/remove-file");
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset the input value when `images` changes
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
      setFiles([]);
    }
  }, [defaultFiles]);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const okFiles = [];
    let hasError = false;
    for (const file of e.target.files ?? []) {
      if (file.size <= field.maxFileSize) {
        okFiles.push(file);
      } else {
        hasError = true;
      }
    }
    if (hasError) {
      setErrors([...field.errors, "One or more files are too large"]);
      e.target.value = "";
      setFiles([]);
    } else {
      setErrors([...field.errors]);
      setFiles(okFiles.map(f => f.name));
    }

    if (field.onChange) field.onChange(e);
  };

  const handleFileRemoval = (idToRemove: string) => {
    setFiles(prevFiles => prevFiles.filter(file => file !== idToRemove));
  };

  const canUploadMore = useMemo(() => {
    return field.limit && field.currentCount
      ? field.currentCount < field.limit
      : true;
  }, [field.limit, field.currentCount]);

  return (
    <div className="col-span-full">
      <label
        htmlFor={field.name}
        className={`block text-sm font-medium leading-6 text-gray-900 ${
          !showPreviews && "flex items-center gap-x-2"
        }`}
      >
        <span>
          {field.label}
          {field.showOptional && (
            <span className="font-normal text-gray-400 text-sm ml-1.5">
              (Optional)
            </span>
          )}
          {field.tooltipText && (
            <Tooltip text={field.tooltipText} position="top">
              <InformationCircleIcon className="h-5 text-gray-400 ml-1.5" />
            </Tooltip>
          )}
        </span>
        <p className="text-gray-500 text-xs">{field.description}</p>
        <p className="text-gray-500 text-xs">
          Max File Size: {field.maxFileSize / 1024 / 1024} MB
        </p>
      </label>
      <div className="mt-2 flex flex-col items-start gap-x-3">
        <div className="flex flex-wrap w-full">
          {showPreviews &&
            !field.disabled &&
            (defaultFiles.length > 0 ? (
              defaultFiles.map((file, index) => {
                const ext = file.uri.split(".").pop() ?? "";
                return (
                  <div key={file.id} className="relative m-2 h-auto w-56">
                    <a
                      href={`/files/${file.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex h-56 w-56 overflow-hidden rounded-md border-2 border-gray-200"
                    >
                      <div className="flex w-full h-auto hover:brightness-75 transition duration-300 justify-center">
                        {IMAGE_EXTENSIONS.includes(ext) ||
                        field.type == "image-upload" ? (
                          <img
                            src={`/files/${file.id}`}
                            className={tvUploadPreview()}
                            alt="banner"
                          />
                        ) : VIDEO_EXTENSIONS.includes(ext) ? (
                          <video
                            src={`/files/${file.id}`}
                            className={tvUploadPreview()}
                          />
                        ) : ext === "pdf" ? (
                          <PdfPreview
                            pdfUrl={`/files/${file.id}`}
                            className={tvUploadPreview()}
                          />
                        ) : ext === "mp3" ? (
                          <div
                            className={tvUploadPreview({
                              className: "justify-center items-center px-2",
                            })}
                          >
                            <audio src={`/files/${file.id}`} controls />
                          </div>
                        ) : (
                          <div
                            className={tvUploadPreview({
                              className:
                                "text-gray-700 flex justify-start items-center p-3",
                            })}
                          >
                            <DocumentIcon
                              className="w-16 min-w-16"
                              aria-hidden="true"
                            />
                            <span className="[text-wrap:pretty]">
                              {file.title || file.uri.split("/").pop()}
                            </span>
                          </div>
                        )}
                      </div>
                    </a>
                    {(field.multiple || field.allowRemoval) && (
                      <button
                        type="button"
                        onClick={() => {
                          fetcher.submit(
                            {
                              file_id: file.id,
                            },
                            {
                              method: "post",
                              action,
                              encType: "multipart/form-data",
                            }
                          );
                          handleFileRemoval(file.id);
                          return false;
                        }}
                      >
                        <XCircleIcon className="h-5 absolute -top-2 -right-2 bg-white rounded-full" />
                      </button>
                    )}
                    {videoTitles && (
                      <>
                        <input
                          type="hidden"
                          name="brand_video_titles.id"
                          value={videoTitles[index].id}
                        />
                        <CrudTextField
                          field={{
                            name: "brand_video_titles.title",
                            label: "",
                            type: "text",
                            placeholder: `Title for video ${index + 1}`,
                            defaultValue: videoTitles[index]
                              ? videoTitles[index].title
                              : "",
                            onChange: e => {
                              field.handleCallback(index, e.target.value);
                            },
                            errors: [],
                            maxCharacters: 30,
                          }}
                        />
                      </>
                    )}
                  </div>
                );
              })
            ) : field.defaultNoValue ? (
              field.defaultNoValue
            ) : (
              <PhotoIcon
                className="h-12 w-12 text-gray-300"
                aria-hidden="true"
              />
            ))}
        </div>
        <div className="flex space-x-3">
          <button
            type="button"
            className={`mt-2 rounded-md px-2.5 py-1.5 text-sm font-semibold shadow-sm ring-1
               ring-inset ring-gray-300 ${
                 field.disabled
                   ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                   : "bg-white  text-gray-900  hover:bg-gray-50"
               }`}
            onClick={() => {
              const fileInput = document.getElementById(field.name);
              fileInput?.click();
            }}
            disabled={!canUploadMore || field.disabled}
          >
            Choose File{field.multiple ? "s" : ""}
          </button>
          {field.limit !== undefined && field.currentCount !== undefined && (
            <span
              className={`text-sm mt-auto mb-1 ${
                field.showMaxError ? "text-red-600 font-bold" : ""
              }`}
            >
              {`${field.currentCount} / ${field.limit}`} max videos
              <span className="text-xs font-extralight">
                {" "}
                (uploads & YouTube URLs)
              </span>
            </span>
          )}
        </div>
        <input
          ref={inputRef}
          id={field.name}
          name={field.name}
          type="file"
          className="sr-only"
          onChange={handleFile}
          multiple={field.multiple}
          accept={field.accept ? `${field.accept}/*` : "/*"}
        />
        <div>
          {files.map(file => (
            <div key={file}>{file}</div>
          ))}
        </div>
        <div className="text-red-600 text-sm py-2" id={`errors-${field.name}`}>
          {errors}
        </div>
      </div>
    </div>
  );
}

export function CrudVideoUrlsField({
  field,
  showPreviews = true,
}: {
  field: CrudFormFieldYoutubeUrls;
  showPreviews?: Boolean;
}) {
  const videos = field.defaultValue;

  const [openAddNewUrlModal, setOpenAddNewUrlModal] = useState(false);

  const handleRemoval = useCallback(
    (toRemove: CloudVideo) => {
      const newVideosList = field.defaultValue.filter(
        video => video !== toRemove
      );
      field.onChange([...newVideosList]);
    },
    [field]
  );

  const handleAdd = useCallback(
    (video: CloudVideo) => {
      setOpenAddNewUrlModal(false);
      const newVideosList = [...field.defaultValue, video];
      field.onChange([...newVideosList]);
    },
    [field]
  );

  const handleTitleChange = useCallback(
    (index: number, title: string) => {
      const newVideosList = [...field.defaultValue];
      newVideosList[index].title = title;
      field.onChange([...newVideosList]);
    },
    [field]
  );

  const canUploadMore = useMemo(() => {
    return field.limit && field.currentCount
      ? field.currentCount < field.limit
      : true;
  }, [field.limit, field.currentCount]);

  return (
    <>
      <AddVideoUrlModal
        isOpen={openAddNewUrlModal}
        onClose={() => setOpenAddNewUrlModal(false)}
        onAddVideo={handleAdd}
      ></AddVideoUrlModal>
      <div className="col-span-full">
        <label
          htmlFor={field.name}
          className={`block text-sm font-medium leading-6 text-gray-900 ${
            !showPreviews && "flex items-center gap-x-2"
          }`}
        >
          {field.label}
          {field.showOptional && (
            <span className="font-normal text-gray-400 text-sm ml-1.5">
              (Optional)
            </span>
          )}
          {field.tooltipText && (
            <Tooltip text={field.tooltipText} position="top">
              <InformationCircleIcon className="h-5 text-gray-400 ml-1.5" />
            </Tooltip>
          )}
        </label>
        <div className="mt-2 flex flex-col items-start gap-x-3">
          <div className="grid grid-cols-1 lg:grid-cols-3 w-max">
            {showPreviews &&
              !field.disabled &&
              (videos.length > 0 ? (
                videos.map((video, index) => {
                  return (
                    <div key={video.url} className="relative m-2">
                      <a href={video.url} target="_blank" rel="noreferrer">
                        <iframe
                          className="h-28 aspect-video p-2"
                          src={video.embedUrl}
                          title="YouTube video player"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        ></iframe>
                      </a>
                      <button
                        type="button"
                        onClick={() => {
                          handleRemoval(video);
                        }}
                      >
                        <XCircleIcon className="h-5 absolute -top-2 -right-2" />
                      </button>
                      <CrudTextField
                        field={{
                          name: "",
                          label: "",
                          type: "text",
                          placeholder: `Title for video ${index + 1}`,
                          defaultValue: video.title,
                          onChange: e => {
                            handleTitleChange(index, e.target.value);
                          },
                          errors: [],
                          maxCharacters: 30,
                        }}
                      />
                    </div>
                  );
                })
              ) : (
                <PhotoIcon
                  className="h-12 w-12 text-gray-300"
                  aria-hidden="true"
                />
              ))}
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              className={`mt-2 rounded-md px-2.5 py-1.5 text-sm font-semibold shadow-sm ring-1
                ring-inset ring-gray-300 ${
                  field.disabled
                    ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                    : "bg-white  text-gray-900  hover:bg-gray-50"
                }`}
              onClick={() => {
                setOpenAddNewUrlModal(true);
              }}
              disabled={!canUploadMore || field.disabled}
            >
              {field.buttonLabel}
            </button>
            {field.limit !== undefined && field.currentCount !== undefined && (
              <span
                className={`text-sm mt-auto mb-1 ${
                  field.showMaxError ? "text-red-600 font-bold" : ""
                }`}
              >
                {`${field.currentCount} / ${field.limit}`} max videos
                <span className="text-xs font-extralight">
                  {" "}
                  (uploads & YouTube URLs)
                </span>
              </span>
            )}
          </div>
          <input
            type="hidden"
            name={field.name}
            value={JSON.stringify(videos)}
          />
        </div>
        <div className="text-red-600 text-sm py-2" id={`errors-${field.name}`}>
          {field.errors}
        </div>
      </div>
    </>
  );
}

export function CrudGroupField({
  field: group,
}: {
  field: CrudFormFieldGroup;
}) {
  return (
    <div className="flex sm:col-span-6">
      {group.fields.map(field => {
        const name = `${group.name}.${field.name}`;
        const formField =
          field.type === "textarea" ? (
            <CrudTextAreaField
              key={field.name}
              field={{ ...field, name, rows: 4 }}
            />
          ) : field.type === "text" ? (
            <CrudTextField key={field.name} field={{ ...field, name }} />
          ) : (
            <div>Field type: {group.type} not supported in group</div>
          );
        return (
          <div className="flex-grow mr-2 mb-2" key={field.name}>
            {formField}
          </div>
        );
      })}
    </div>
  );
}

export function CrudCheckboxField({
  field,
  className,
  onChange,
}: {
  field: CrudFormFieldCheckbox;
  className?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  field.value = field.value ?? "true";

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (field.reactiveDefaultChecked && inputRef.current) {
      inputRef.current.checked = !!field.defaultChecked;
    }
  }, [field.defaultChecked, field.reactiveDefaultChecked]);

  const isMobile = useIsMobile();

  return (
    <div className={`sm:col-span-4 ${className}`}>
      <div className="flex items-center gap-x-3">
        <div className="flex h-6 items-center">
          <input
            id={field.name}
            name={field.name}
            type="checkbox"
            className={`cursor-pointer h-4 w-4 rounded border-gray-30
             ${
               field.disabled
                 ? "cursor-not-allowed focus:ring-gray-500 accent-gray-500 text-gray-500"
                 : "focus:ring-sky-600 accent-sky-600 text-sky-600"
             }
             `}
            value={field.value}
            defaultChecked={field.defaultChecked}
            onChange={onChange}
            disabled={field.disabled}
            ref={inputRef}
          />
        </div>
        <div className="text-sm leading-6">
          <div className="flex items-center">
            {field.showTooltipOnLabel && field.tooltipText ? (
              <Tooltip
                text={field.tooltipText}
                position={isMobile ? "rightTop" : "top"}
                size={isMobile ? "small" : "medium"}
              >
                <label htmlFor="comments" className="font-medium text-gray-900">
                  {field.label}
                </label>
              </Tooltip>
            ) : (
              <label htmlFor="comments" className="font-medium text-gray-900">
                {field.label}
              </label>
            )}

            {!field.showTooltipOnLabel && field.tooltipText && (
              <Tooltip text={field.tooltipText} position="top">
                <InformationCircleIcon className="h-5 text-gray-400 ml-1.5" />
              </Tooltip>
            )}
          </div>
          {field.description && (
            <p className="text-gray-500">{field.description}</p>
          )}
        </div>
      </div>
      <p
        className="mt-3 text-sm leading-6 text-red-600"
        id={`errors-${field.name}`}
      >
        {field.errors?.join(" ")}
      </p>
    </div>
  );
}

export function CrudRadioButtonField({
  field,
  className,
}: {
  field: CrudFormFieldRadioButton;
  className?: string;
}) {
  return (
    <div className={`flex gap-x-3 sm:col-span-4 ${className}`}>
      <div className="flex h-6 items-center">
        <input
          id={field.name}
          type="radio"
          className={`h-4 w-4 border-gray-30  ${
            field.disabled
              ? "cursor-not-allowed focus:ring-gray-500 accent-gray-500 text-gray-500"
              : "focus:ring-sky-600 accent-sky-600 text-sky-600"
          }`}
          name={field.name}
          value={field.value}
          onChange={field.onChange}
          defaultChecked={field.defaultChecked}
          checked={field.checked}
          disabled={field.disabled}
        />
      </div>
      <div className="text-sm">
        <div className="flex items-center">
          <label htmlFor={field.name} className="font-medium text-gray-900">
            {field.label}
          </label>
          {field.tooltipText && (
            <Tooltip text={field.tooltipText} position="top">
              <InformationCircleIcon className="h-5 text-gray-400 ml-1.5" />
            </Tooltip>
          )}
        </div>
        <p className="text-gray-500">{field.description}</p>
        <p
          className="mt-3 text-sm leading-6 text-red-600"
          id={`errors-${field.name}`}
        >
          {field.errors?.join(" ")}
        </p>
      </div>
    </div>
  );
}

export function CrudDateField({
  field,
  className,
  minDate,
  quickDelete = false,
}: {
  field: CrudFormFieldText;
  className?: string;
  minDate?: string;
  quickDelete?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (
      field.reactiveDefaultValue &&
      inputRef.current &&
      field.defaultValue !== undefined
    ) {
      inputRef.current.value = field.defaultValue as string;
    }
  }, [field.defaultValue, field.reactiveDefaultValue]);

  return (
    <div className={`col-span-full ${className}`}>
      <label
        htmlFor={field.name}
        className="flex items-center text-sm font-medium leading-6 text-gray-900"
      >
        {field.label}
        {field.showOptional && (
          <span className="font-normal text-gray-400 text-sm ml-1.5">
            (Optional)
          </span>
        )}
        {field.tooltipText && (
          <Tooltip text={field.tooltipText} position="top">
            <InformationCircleIcon className="h-5 text-gray-400 ml-1.5" />
          </Tooltip>
        )}
      </label>

      <div className="relative mt-2">
        <input
          type="date"
          name={field.name}
          id={field.name}
          defaultValue={field.defaultValue}
          placeholder={field.placeholder}
          min={minDate}
          className={tvField({ error: field.errors.length > 0 })}
          disabled={field.disabled}
          onChange={field.onChange}
          onKeyDown={field.onKeyDown}
          maxLength={field.maxCharacters}
          ref={inputRef}
        />
        {quickDelete && (
          <button
            id="clear-date"
            className="absolute top-2 right-0 mr-10 text-sky-500"
            onClick={e => {
              e.preventDefault();
              if (inputRef.current) {
                // Dispatch a change event to the input to trigger the onChange handler
                const input = document.getElementById(field.name);
                if (input) {
                  const desc = Object.getOwnPropertyDescriptor(
                    HTMLInputElement.prototype,
                    "value"
                  );
                  desc?.set?.call(input, "");
                  input.dispatchEvent(new Event("change", { bubbles: true }));
                }
                inputRef.current.value = "";
              }
            }}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}
      </div>
      <p className="mt-1 text-xs text-gray-500 px-2">{field.description}</p>
      <p
        className="mt-3 text-sm leading-6 text-red-600"
        id={`errors-${field.name}`}
      >
        {field.errors?.join(" ")}
      </p>
    </div>
  );
}

const tvFormSection = tv({
  base: "grid grid-cols-1 gap-y-2 @lg:grid-cols-6",
  variants: {
    spacing: {
      comfortable: "gap-y-8",
      medium: "gap-y-4",
      compact: "gap-y-2",
    },
  },
  defaultVariants: {
    spacing: "comfortable",
  },
});

export function FormSection({
  id,
  title,
  subtitle,
  children,
  manager = false,
  spacing = "comfortable",
  border = true,
  noPadding = false,
  classNames = {
    title: "",
    subtitle: "",
  },
}: {
  id?: string;
  title: string | ReactNode;
  children: ReactNode;
  subtitle?: ReactNode;
  manager?: boolean;
  spacing?: "comfortable" | "compact" | "medium";
  border?: boolean;
  noPadding?: boolean;
  classNames?: {
    title?: string;
    subtitle?: string;
  };
}) {
  return manager ? (
    <div className="space-y-8 @container">
      <div className="px-4 @lg:px-0">
        <h2
          id={id}
          className={
            classNames.title ||
            "text-lg @xl:text-3xl font-semibold leading-7 text-gray-900"
          }
        >
          {title}
        </h2>
        <div
          className={
            classNames.subtitle || "mt-1 text-base leading-6 text-gray-600"
          }
        >
          {subtitle}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-y-6 @lg:grid-cols-6">{children}</div>
    </div>
  ) : (
    <div className="space-y-10 divide-y divide-gray-900/10 @container">
      <div className="grid grid-cols-1 gap-x-8 gap-y-8 @lg:grid-cols-3 mb-6">
        <div className={noPadding ? "" : "pt-6"}>
          <h2
            id={id}
            className={
              classNames.title ||
              "text-base font-semibold leading-7 text-gray-900"
            }
          >
            {title}
          </h2>
          <div
            className={
              classNames.subtitle || "mt-1 text-sm leading-6 text-gray-600"
            }
          >
            {subtitle}
          </div>
        </div>

        <div
          className={`bg-white @lg:rounded-xl @lg:col-span-2 ${
            border ? "ring-1 ring-gray-900/5 shadow-sm" : ""
          }`}
        >
          <div className={noPadding ? "" : "px-4 py-6 @md:p-8"}>
            <div className={tvFormSection({ spacing })}>{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CrudForm({ config }: { config: CrudFormConfig }) {
  return (
    <FormWithUploads>
      {config.showToast && config.submitSuccess !== undefined && (
        <Toast
          variant={config.submitSuccess ? "success" : "error"}
          message={
            config.customToastMessage ??
            (config.submitSuccess
              ? "Success!"
              : "There were problems submitting your form. Please try again.")
          }
        />
      )}
      {config.sections.map((section, i) => (
        <FormSection
          key={section.title}
          title={section.title}
          subtitle={section.subtitle}
          manager={config.manager}
        >
          {section.fields.map((field, j) => {
            const key = `${i}-${j}-${field.name}`;
            switch (field.type) {
              case "website":
                return <CrudWebsiteField key={key} field={field} />;
              case "textarea":
                return <CrudTextAreaField key={key} field={field} />;
              case "select":
                return <CrudSelectField key={key} field={field} />;
              case "checkbox":
                return <CrudCheckboxField key={key} field={field} />;
              case "radio":
                return <CrudRadioButtonField key={key} field={field} />;
              case "file-upload":
              case "image-upload":
                return <CrudFileUpload key={key} field={field} />;
              case "group":
                return <CrudGroupField key={key} field={field} />;
              case "autocomplete":
                return <CrudAutocompleteField key={key} field={field} />;
              case "custom":
                return (
                  <React.Fragment key={key}> {field.render()} </React.Fragment>
                );
              default:
                return <CrudTextField key={key} field={field} />;
            }
          })}
        </FormSection>
      ))}
      <div className="flex items-center justify-end py-4">
        {config.manager ? (
          <CTA variant="sky-shadow" className="flex" type="submit">
            Save
          </CTA>
        ) : (
          <Button className="flex" type="submit" id="save-button">
            Save
          </Button>
        )}
      </div>
    </FormWithUploads>
  );
}
